/**
 * Safely gets a nested value from an object.
 * @param obj The object to query.
 * @param path The path of the property to retrieve.
 * @param defaultValue The value to return for `undefined` resolved values.
 * @returns The resolved value or the default value.
 * @example get({ user: { profile: { name: 'John' } } }, 'user.profile.name') => 'John'
 * @example get({ user: { profile: { name: 'John' } } }, 'user.profile.age', 25) => 25
 */
export const get = <
  T extends object,
  TPath extends string,
  TDefault = undefined
>(
  obj: T,
  path: TPath,
  defaultValue?: TDefault
): any => {
  const keys = Array.isArray(path) ? path : path.split('.')
  let result: any = obj
  for (const key of keys) {
    result = result?.[key]
    if (result === undefined) return defaultValue
  }
  return result ?? defaultValue
}

/**
 * Checks if a path exists in an object
 *
 * @param obj - The object to check
 * @param path - The path to check (dot notation string or array)
 * @returns true if the path exists, false otherwise
 *
 * @example
 * const obj = { user: { profile: { name: 'John' } } }
 * has(obj, 'user.profile.name') // true
 * has(obj, 'user.profile.age') // false
 * has(obj, ['user', 'profile', 'name']) // true
 */
export const has = (
  obj: Record<string, unknown>,
  path: string | string[]
): boolean => {
  const keys = Array.isArray(path) ? path : path.split('.')
  let current: unknown = obj

  for (const key of keys) {
    if (
      current === null ||
      current === undefined ||
      !Object.prototype.hasOwnProperty.call(current, key)
    ) {
      return false
    }
    current = (current as Record<string, unknown>)[key]
  }

  return true
}

/**
 * Compara se dois objetos são iguais em suas propriedades de primeiro nível
 * @param a Primeiro objeto
 * @param b Segundo objeto
 * @returns true se os objetos têm as mesmas propriedades com os mesmos valores
 * @example shallowEqual({ a: 1, b: 2 }, { a: 1, b: 2 }) => true
 */
export function shallowEqual<T>(a?: T | null, b?: T | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  const ka = Object.keys(a)
  const kb = Object.keys(b)
  if (ka.length !== kb.length) return false
  for (const k of ka) {
    if (a[k as keyof T] !== b[k as keyof T]) return false
  }
  return true
}

/**
 * Creates an object composed of the picked object properties.
 */
export const pick = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> => {
  const result = {} as Pick<T, K>
  for (const key of keys) {
    if (key in obj) result[key] = obj[key]
  }
  return result
}

/**
 * Checks if `value` is a function.
 */
export const isFunction = (value: unknown): value is (...args: unknown[]) => unknown =>
  typeof value === 'function'
